import React, { useRef, useState, useEffect } from "react";
import useRecorder from "../../utils/useRecorder";
import AudioPlayer from "../../utils/audioPlayer";
import { useDispatch } from "react-redux";
import { uploadAudio } from "../../Redux/Action/apiAction";

const SpeakingSection = ({ section, flattenedQuestions, submitAnswer, onRequestNextSection, assignmentId }) => {
  const dispatch = useDispatch();
  const timerRef = useRef(null);
  const playerRef = useRef(null);

  const [index, setIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(61);
  const [recordedOnce, setRecordedOnce] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);

  const { startRecording, stopRecording, reset, isRecording, audioBlob, audioURL, durationSeconds } = useRecorder();
  
  const isCompleted = (q) => {
    if (!q?.previous_answer?.answer) return false;
    const data = q.previous_answer.answer.answer_data;
    if (data?.status === "completed") return true;
    if (typeof data === "string" && data.trim() !== "") return true;
    if (typeof data === "object" && Object.keys(data).length > 0) return true;
    return false;
  };

  useEffect(() => {
    let nextIndex = index;

    while (
      nextIndex < flattenedQuestions.length &&
      isCompleted(flattenedQuestions[nextIndex])
    ) {
      nextIndex++;
    }

    if (nextIndex !== index) {
      if (nextIndex >= flattenedQuestions.length) {
        onRequestNextSection && onRequestNextSection();
      } else {
        setIndex(nextIndex);
      }
    }
  }, [section, index, flattenedQuestions]);

  useEffect(() => {
    setIndex(0);
    setRecordedOnce(false);
    setTimeLeft(61);
    setIsUploading(false);
    setError(null);
  }, [section]);

  const current = flattenedQuestions[index];

  if (!current) return <div className="language-right-section">No questions</div>;

  const startTimedRecording = () => {
    reset();
    setRecordedOnce(false);
    setTimeLeft(61);
    setError(null);

    startRecording();

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          stopRecording();
          setRecordedOnce(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleUploadAndSubmit = async () => {
    if (!audioBlob) {
      setError("No recording available");
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("audio_file", audioBlob, `recording_${Date.now()}.webm`);
      formData.append("assessment_id", assignmentId);
      formData.append("question_id", current.id);
      formData.append("question_type", current.content?.question_type || "");
      formData.append("assignment_question_id", current.assignment_question_id);

      const res = await dispatch(uploadAudio(formData));

      if (res?.success && res.payload?.audio_url) {

        const answer = {
          answer_data: {
            type: "audio_recording",
            audio_url: res.payload.audio_url,
            duration_seconds: res.payload.duration_seconds ?? durationSeconds,
            status: "completed",
            timestamp: new Date().toISOString(),
          }
        };

        submitAnswer({
          assignment_question_id: current.assignment_question_id,
          question_id: current.id,
          answer
        });

        setIsUploading(false);

        if (index < flattenedQuestions.length - 1) {
          setIndex(index + 1);
          setRecordedOnce(false);
          reset();
        } else {
          onRequestNextSection && onRequestNextSection();
        }
      } else {
        setIsUploading(false);
        setError("Upload failed");
      }
    } catch (err) {
      setIsUploading(false);
      setError("Upload failed");
      console.error("Upload error", err);
    }
  };


  return (
    <>
      <div className="language-left-section speaking" style={{gap:'12px'}}>
        <img src="/images/speaker-gray.svg" alt="" width={56} height={56} />
        <h4 style={{ marginBottom: "0" }}>
          {current?.content?.question_type === "reading_fluency_individual"? "Speaking" : "Reading"}
        </h4>
        {current.content?.instructions && (
          <p className="instructions">{current.content.instructions}</p>
        )}
        <p>{current.text}</p>
        {recordedOnce && audioURL && (
          <AudioPlayer audioUrl={audioURL} ref={playerRef} onPlayStateChange={setIsPlaying} />
        )}
      </div>
      <div className="language-right-section speaking-right">
        {(!isRecording && recordedOnce) ? <p style={{marginBottom:'0'}}>Record your response by clicking below toggle</p> : <p style={{marginBottom:'0'}}>Submit answer if ok</p>}
        
        <div className="recorder">
          <div className="text-center">
            <div className="wight-mic-bg" style={{ cursor: "pointer" }}>
            {!isRecording && !recordedOnce && (
              <img src="/images/mic-icon.svg" alt="" width={56} height={56} onClick={startTimedRecording} />
            )}

            {isRecording && (
              <img src="/images/reddot-img.svg" alt="" width={71} height={71} onClick={() => {clearInterval(timerRef.current); stopRecording(); setRecordedOnce(true);}} />
            )}

            {!isRecording && recordedOnce && (
              <img src={ isPlaying ? "/images/pause-icon.svg" : "/images/play-icon.svg" } alt="" width={56} height={56} onClick={() => {
                  if (!playerRef.current) return;
                  if (isPlaying) playerRef.current.pause();
                  else playerRef.current.play();
                }} />
            )}
          </div>
          {!isRecording && recordedOnce && (<span>Play to listen</span>)}
          </div>

          {isRecording && (
            <div className="recording-state">
              <div className="big-dot" />
              <p>Recording... {timeLeft}s left</p>
            </div>
          )}

          {recordedOnce && audioURL && (
            <>
              <div className="recorder-actions">
                <button className="primary" onClick={handleUploadAndSubmit} disabled={isUploading}>{isUploading ? "Uploading…" : "Submit"}</button>
                <p onClick={() => {reset(); setRecordedOnce(false);}}><img src="/images/refresh-outline.svg" alt="" width={16} height={16} />Replay question and record again</p>
              </div>
              {error && <p className="error" style={{ marginTop: 8 }}>{error}</p>}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default SpeakingSection;